import type { CaseStudy, TranslationType, Stat } from "./types"

export const stats: Stat[] = [
  { value: "50", suffix: "+", label: "Strategic Engagements" },
  { value: "15", suffix: "+", label: "Years Experience" },
  { value: "6", suffix: "", label: "Industry Verticals" },
]

export const clientLogos = [
  "Innodisk",
  "Chroma",
  "MediaTek",
  "Advantech",
  "Lotus Pharma",
  "Kikawa",
  "Aetina",
  "Wiltrom",
  "Inventec",
  "Quaser",
  "Ørsted",
  "Okuma",
]

export const caseStudies: CaseStudy[] = [
  // === FEATURED LARGE CASE STUDIES (Alphabetically ordered) ===
  {
    id: "advantech",
    client: "Advantech",
    title: "Hardware to Solutions",
    industry: "Industrial IoT",
    translationType: "legacy-future",
    misunderstanding: "Industrial computing with dated image",
    hiddenTruth: "40 years of industrial mastery powering the intelligent edge.",
    translation: "Bridging industrial heritage with AI-driven future.",
    tag: "Corporate Transformation",
    tagline: "Redefining a global IoT leader's value proposition.",
    services: "Brand Strategy • Corporate Transformation • Brand Architecture",
    gap: "Complexity → Story",
    tier: "large",
    image: "/industrial-iot-smart-factory-copper.jpg",
    impact: [
      "Internal alignment across global offices",
      "Shared language for evolved value proposition",
      "Strategic conversations replacing component pricing discussions",
    ],
    featured: true,
    year: "2023",
    role: "Strategic Positioning Lead",
    duration: "7 months",
    challenge:
      "Advantech's 40 years of industrial computing excellence came with an outdated brand image. In the AI and IoT era, the company risked being seen as legacy industrial computing rather than the intelligent edge pioneer they've become. The challenge was connecting their deep heritage with their AI-driven future.",
    approach:
      "Developed 'Bridging industrial heritage with AI-driven future' positioning framework. Created narrative that celebrates 40 years of industrial mastery as foundation for intelligent edge solutions. Repositioned legacy strength as competitive advantage—deep industrial knowledge meeting cutting-edge AI capabilities.",
    outcome:
      "Modern brand positioning successfully launched across all markets. Achieved recognition as AIoT leadership brand. Secured smart city partnerships and positioned as strategic partner for industrial digital transformation, not just hardware vendor.",
    images: ["/images/case-studies/advantech-1.svg"],
    metrics: [
      { label: "Years of Heritage", value: "40" },
      { label: "Market Position", value: "Top 3" },
      { label: "AIoT Recognition", value: "Elevated" },
      { label: "Strategic Partnerships", value: "15+" },
    ],
  },
  {
    id: "aetina",
    client: "Aetina",
    title: "Edge ON. AI Beyond.",
    industry: "Edge AI / Embedded Computing",
    translationType: "tech-humanity",
    misunderstanding: "Just another embedded computing vendor",
    hiddenTruth: "Edge AI enabler accelerating deployment from concept to production.",
    translation: "Empowering intelligent edge computing for next-gen applications.",
    tag: "Edge AI Platform",
    tagline: "Empowering intelligent edge computing for next-gen applications.",
    services: "Brand Strategy • Product Positioning • Developer Marketing",
    gap: "Technology → Humanity",
    tier: "large",
    image: "/aetina-edge-ai-computing.jpg",
    caseStudyUrl: "https://red-peak.com/aetina/",
    impact: [
      "Clear market positioning as edge AI enabler",
      "Enhanced developer community engagement",
      "Stronger partnership narrative with NVIDIA",
    ],
    year: "2024",
    role: "Product Positioning Lead",
    duration: "5 months",
    challenge:
      "Aetina's edge AI platforms competed in crowded market where technical specs alone didn't differentiate. NVIDIA partnership provided foundation but not distinct identity.",
    approach:
      '"Edge ON. AI Beyond." positioning emphasized enablement over hardware. Showcased how Aetina accelerates AI deployment from concept to production. Developed developer-focused content and ecosystem narrative.',
    outcome:
      "Clear market positioning as edge AI enabler. Enhanced developer community engagement and stronger partnership narrative with NVIDIA.",
    images: ["/images/case-studies/aetina-1.svg"],
    metrics: [
      { label: "Developer Engagement", value: "Elevated" },
      { label: "NVIDIA Partnership", value: "Strengthened" },
      { label: "Market Position", value: "Top 5" },
      { label: "Lead Generation", value: "Strengthened" },
    ],
    featured: false,
  },
  {
    id: "calyx",
    client: "Calyx",
    title: "Striking the Balance Between Biology and Technology",
    industry: "IoT / Multi-gas Detection",
    translationType: "tech-humanity",
    misunderstanding: "Unknown sensor technology company",
    hiddenTruth:
      "Biological systems inspire technology that enables breakthrough detection capabilities.",
    translation: "Bridging biological wisdom with technological precision.",
    tag: "Brand Foundation",
    tagline: "Striking the balance between biology and technology.",
    services: "Brand Strategy • Visual Identity • Website Launch • Investor Materials",
    gap: "Technology → Humanity",
    tier: "large",
    image: "/edge-ai-technology-circuit-board-blue-glow.jpg",
    caseStudyUrl: "https://red-peak.com/calyx/",
    impact: [
      "Strategic naming derived from botanical terminology",
      "Visual identity optimized for technical and non-technical audiences",
      "Successful brand launch supporting funding pitches",
      "Logo designed for legibility on small device surfaces",
    ],
    year: "2024",
    role: "Brand Strategy Lead",
    duration: "6 months",
    challenge:
      "Calyx, a Silicon Valley-based IoT startup specializing in multi-gas detection technology inspired by biological systems, required comprehensive branding to establish credibility and communicate its innovative approach to a market unfamiliar with this technology type.",
    approach:
      "Developed strategic naming ('calyx' derived from botanical terminology) and visual identity system designed for clarity across technical and non-technical audiences. Created logo optimized for legibility on small device surfaces, supporting the product's physical requirements.",
    outcome:
      "Delivered complete brand foundation within six-month timeline, supporting funding pitches and product launches. Established visual identity that simplified complex sensor technology while maintaining sophistication and credibility in a rigid, technical industry.",
    images: ["/images/case-studies/calyx-1.svg"],
    metrics: [
      { label: "Timeline", value: "6 months" },
      { label: "Market Position", value: "Established" },
      { label: "Brand Clarity", value: "Achieved" },
      { label: "Device Legibility", value: "Optimized" },
    ],
    featured: false,
  },
  {
    id: "chroma",
    client: "Chroma",
    title: "Advancing Excellence",
    industry: "Precision Instruments",
    translationType: "data-story",
    misunderstanding: "Technical measurement company with complex specifications",
    hiddenTruth: "Behind every number is a story of precision enabling human progress.",
    translation: "From data sheets to narratives of precision that matter.",
    tag: "Global Positioning",
    tagline: "Behind every breakthrough in precision testing.",
    services: "Brand Strategy • Marketing • Global Communications",
    gap: "Vision → Strategy",
    tier: "large",
    image: "/chroma-lab-equipment.jpg",
    caseStudyUrl: "https://red-peak.com/chroma/",
    impact: [
      "Elevated brand perception in global markets",
      "Enhanced engagement with enterprise customers",
      "Differentiated in commoditized testing equipment market",
    ],
    featured: true,
    year: "2023",
    role: "Brand Narrative Strategist",
    duration: "6 months",
    challenge:
      "Chroma's precision test equipment was recognized for technical excellence but struggled to communicate value beyond specifications. In global markets, engineers understood the numbers, but decision-makers and C-suite executives needed a compelling narrative that transcended technical documentation.",
    approach:
      "Positioned Chroma as the enabler of innovation—'Behind every breakthrough in precision testing.' Shifted focus from equipment features to customer success stories. Developed narrative framework that translates technical precision into business value, creating content that bridges engineering excellence with strategic impact.",
    outcome:
      "Elevated brand perception in global markets, particularly among enterprise decision-makers. Enhanced engagement with C-suite executives and procurement teams. Successfully differentiated Chroma in a commoditized testing equipment market through storytelling that connects technical capabilities to innovation outcomes.",
    images: ["/images/case-studies/chroma-1.svg"],
    metrics: [
      { label: "Market Segments", value: "12+" },
      { label: "Enterprise Engagement", value: "Elevated" },
      { label: "Brand Perception", value: "Strengthened" },
      { label: "Premium Positioning", value: "Achieved" },
    ],
  },
  {
    id: "ikala",
    client: "iKala",
    title: "AI-Powered Marketing Intelligence",
    industry: "AI MarTech / E-commerce",
    translationType: "tech-humanity",
    misunderstanding: "Just another AI marketing platform",
    hiddenTruth: "AI-powered marketing intelligence transforming brand-consumer connections.",
    translation: "Transforming how brands connect with consumers through AI.",
    tag: "MarTech Innovation",
    tagline: "Transforming how brands connect with consumers through AI.",
    services: "Brand Strategy • Product Positioning • Market Communication",
    gap: "Technology → Humanity",
    tier: "large",
    image: "/ikala-ai-martech.jpg",
    impact: [
      "Improved engagement with enterprise marketing teams",
      "Clear differentiation in MarTech ecosystem",
      "Enhanced brand perception as strategic partner",
    ],
    year: "2023",
    role: "Product Strategy Lead",
    duration: "4 months",
    challenge:
      "iKala's AI marketing platform needed to stand out in saturated MarTech landscape. Technical capabilities were strong but value proposition wasn't resonating with marketing decision-makers.",
    approach:
      "Repositioned from AI platform to marketing intelligence partner. Translated technical features into business outcomes. Created narrative connecting AI capabilities with CMO priorities.",
    outcome:
      "Improved engagement with enterprise marketing teams. Clear differentiation in MarTech ecosystem and enhanced brand perception as strategic partner.",
    images: ["/images/case-studies/ikala-1.svg"],
    metrics: [
      { label: "Enterprise Deals", value: "Accelerated" },
      { label: "CMO Engagement", value: "Deepened" },
      { label: "Differentiation", value: "Clear" },
      { label: "Partner Perception", value: "Strategic" },
    ],
  },
  {
    id: "innodisk",
    client: "Innodisk",
    title: "Architect Intelligence",
    industry: "Edge AI / Industrial Storage",
    translationType: "tech-humanity",
    misunderstanding: "Just another component supplier in a crowded market",
    hiddenTruth:
      "They don't sell parts. They architect the intelligence layer for Edge AI systems.",
    translation: '"Architect Intelligence" — From vendor to strategic partner in the AI ecosystem.',
    tag: "Market Repositioning",
    tagline: "From component vendor to AI ecosystem architect.",
    services: "Brand Strategy • Visual Identity • Website • Internal Alignment",
    gap: "Technology → Humanity",
    tier: "large",
    image: "/innodisk-edge-ai.jpg",
    impact: [
      "Taiwan Excellence Awards recognition",
      "Record revenue with significant YoY growth",
      "NVIDIA Elite Partner, Intel Gold Partner status",
      "From supplier to ecosystem-level peer",
    ],
    featured: true,
    year: "2024",
    role: "Strategic Narrative Lead",
    duration: "8 months",
    challenge:
      "Innodisk was perceived as a commodity storage supplier in a highly competitive market. Despite their advanced Edge AI capabilities and partnerships with NVIDIA and Intel, they struggled to differentiate from traditional component vendors. The market saw them as 'another SSD manufacturer' rather than an AI infrastructure innovator.",
    approach:
      "Conducted deep stakeholder interviews across engineering, sales, and executive teams. Analyzed competitive positioning in the Edge AI space. Developed the 'Architect Intelligence' narrative framework that repositioned Innodisk from component supplier to strategic AI ecosystem partner. Created messaging hierarchies, customer journey maps, and content strategies that emphasized their role in enabling Edge AI deployments.",
    outcome:
      "The new narrative framework has been adopted across all customer touchpoints—from website and sales materials to trade show presentations. Innodisk achieved NVIDIA Elite Partner status and Intel Gold Partner recognition in 2024, with record-breaking revenue growth. The 'Architect Intelligence' positioning now drives their market strategy.",
    images: ["/images/case-studies/innodisk-1.svg"],
    metrics: [
      { label: "Partner Tier Elevation", value: "Elite" },
      { label: "Revenue Growth YoY", value: "Accelerated" },
      { label: "Brand Perception Shift", value: "Elevated" },
      { label: "Sales Cycle Reduction", value: "Shortened" },
    ],
    testimonial: {
      quote:
        "Raychard helped us see what we couldn't articulate ourselves. We're not selling storage—we're architecting intelligence at the edge. That clarity transformed our market position.",
    },
  },
  {
    id: "inventec",
    client: "Inventec",
    title: "Legacy to Emerging Tech",
    industry: "Electronics Manufacturing",
    translationType: "legacy-future",
    misunderstanding: "Traditional ODM manufacturer",
    hiddenTruth: "Evolution from manufacturing excellence to innovation partner.",
    translation: "Transforming perception from ODM to innovation partner.",
    tag: "Brand Evolution",
    tagline: "Transforming perception from ODM to innovation partner.",
    services: "Brand Strategy • Corporate Positioning • Innovation Communication",
    gap: "Vision → Strategy",
    tier: "large",
    image: "/inventec-electronics-manufacturing.jpg",
    caseStudyUrl: "https://red-peak.com/inventec/",
    impact: [
      "Elevated brand positioning with technology partners",
      "Increased engagement in emerging technology sectors",
      "Enhanced recruitment of top engineering talent",
    ],
    year: "2023",
    role: "Corporate Positioning Lead",
    duration: "6 months",
    challenge:
      "Inventec's evolution from traditional manufacturing to AI, IoT, and cloud solutions wasn't reflected in market perception. Strong ODM heritage became a barrier to emerging tech conversations.",
    approach:
      "Developed dual narrative honoring manufacturing excellence while showcasing innovation capabilities. Created storytelling framework connecting legacy reliability with future-facing solutions.",
    outcome:
      "Elevated brand positioning with technology partners. Increased engagement in emerging technology sectors and enhanced recruitment of top engineering talent.",
    images: ["/images/case-studies/inventec-1.svg"],
    metrics: [
      { label: "Tech Partnerships", value: "Expanded" },
      { label: "Talent Acquisition", value: "Strengthened" },
      { label: "Innovation Perception", value: "Elevated" },
      { label: "Sector Expansion", value: "3 new" },
    ],
  },
  {
    id: "kikawa",
    client: "Kikawa",
    title: "Engineering Flow Intelligence",
    industry: "Industrial Equipment / Fluid Management",
    translationType: "legacy-future",
    misunderstanding: "Traditional manufacturing with outdated image",
    hiddenTruth: "50 years of mastery meeting tomorrow's intelligent manufacturing.",
    translation: "Connecting heritage to tomorrow.",
    tag: "Industrial Innovation",
    tagline: "Precision water pump solutions for industrial applications.",
    services: "Brand Strategy • Product Communication • Technical Marketing",
    gap: "Technology → Humanity",
    tier: "large",
    image: "/kikawa-water-pump.jpg",
    impact: [
      "Premium positioning in industrial pump market",
      "Stronger relationships with systems integrators",
      "Expanded market presence across Asia-Pacific",
    ],
    year: "2023",
    role: "Brand Systems Architect",
    duration: "12 months",
    challenge:
      "Kikawa, a 50-year manufacturing excellence company, faced an outdated brand image that didn't reflect their advanced capabilities in intelligent manufacturing. The brand lacked systematic management processes—decisions were made inconsistently, resulting in diluted brand identity and difficulty attracting next-generation customers and talent.",
    approach:
      "Designed comprehensive brand management system (BrandOS framework) covering positioning, architecture, messaging, visual identity, and governance. Created 'Connecting heritage to tomorrow' narrative that celebrates 50 years of mastery while positioning for intelligent manufacturing future. Developed playbooks and decision frameworks for consistent brand execution.",
    outcome:
      "Transformed Kikawa's brand operations with scalable system supporting growth while maintaining brand integrity. Reduced time-to-market for new products significantly. Improved cross-functional alignment and successfully attracted younger engineering talent. Positioned as digital transformation leader in traditional manufacturing sector.",
    images: ["/images/case-studies/kikawa-1.svg"],
    metrics: [
      { label: "System Modules", value: "5" },
      { label: "Time-to-Market", value: "Accelerated" },
      { label: "Brand Playbooks", value: "8" },
      { label: "Stakeholder Satisfaction", value: "High" },
    ],
    testimonial: {
      quote:
        "Raychard built us a complete operating system for our brand. It's transformed how we work and how the market sees us.",
    },
  },
  {
    id: "lotus-pharma",
    client: "Lotus Pharma",
    title: "Global Brand Unification",
    industry: "Healthcare / Pharmaceuticals",
    translationType: "local-global",
    misunderstanding: "Regional pharmaceutical player",
    hiddenTruth: "Bridging Eastern wisdom with Western pharmaceutical standards.",
    translation: "Helping Asian leaders speak to the world.",
    tag: "Brand Unification",
    tagline: "Aligning regional operations under one cohesive brand.",
    services: "Brand Strategy • Visual Identity • Internal Alignment • Market Positioning",
    gap: "Vision → Strategy",
    tier: "large",
    image: "/lotus-pharma-healthcare.jpg",
    impact: [
      "Unified brand presence across 15+ markets",
      "Enhanced recognition in international partnerships",
      "Streamlined marketing operations and reduced costs",
      "Strengthened position for M&A opportunities",
    ],
    year: "2022",
    role: "Global Brand Strategist",
    duration: "10 months",
    challenge:
      "Lotus Pharma operated across multiple Asian markets with fragmented brand identities. Regional variations diluted brand equity and complicated international expansion into Western markets. The company needed to present a unified global voice while honoring regional nuances, bridging Eastern pharmaceutical heritage with Western regulatory standards.",
    approach:
      "Conducted comprehensive brand audit across 15+ markets. Developed unified visual system and messaging architecture that positioned Lotus Pharma as a bridge between Eastern wisdom and Western pharmaceutical excellence. Created phased rollout strategy with regional champions to ensure authentic adoption.",
    outcome:
      "Unified brand presence successfully launched across all markets. Enhanced recognition in international partnerships and regulatory bodies. Streamlined marketing operations reduced costs substantially. Strengthened position for M&A opportunities and international clinical trial partnerships.",
    images: ["/images/case-studies/lotus-pharma-1.svg"],
    metrics: [
      { label: "Markets Unified", value: "15+" },
      { label: "Cost Reduction", value: "Reduced" },
      { label: "Partnership Growth", value: "Expanded" },
      { label: "Brand Consistency", value: "Achieved" },
    ],
    testimonial: {
      quote:
        "Raychard didn't just unify our brand—he helped us find our voice on the global stage while staying true to our Asian roots.",
    },
  },
  {
    id: "mediatek",
    client: "MediaTek",
    title: "Edge AI Leadership",
    industry: "Semiconductor",
    translationType: "perception-reality",
    misunderstanding: "Budget alternative in mobile chipsets",
    hiddenTruth: "Pioneer in democratizing advanced technology for billions.",
    translation: "Closing the gap between what they are and what people see.",
    tag: "Technology Communication",
    tagline: "Positioning semiconductor innovation for global markets.",
    services: "Brand Strategy • Campaign Development • Technology Communication",
    gap: "Technology → Humanity",
    tier: "large",
    image: "/mediatek-semiconductor-chip.jpg",
    caseStudyUrl: "https://red-peak.com/mediatek/",
    impact: [
      "Established thought leadership in Edge AI",
      "Effective engagement with device manufacturers",
      "Differentiated positioning in competitive semiconductor market",
    ],
    featured: true,
    year: "2024",
    role: "Employer Brand Strategist",
    duration: "8 months",
    challenge:
      "MediaTek was perceived as a budget alternative in mobile chipsets despite being a global semiconductor leader. The company needed to strengthen employer brand in competitive US and India tech markets to attract top engineering talent, competing against better-known brands like Qualcomm and NVIDIA.",
    approach:
      "Developed differentiated employer value proposition (EVP) based on extensive employee interviews and competitive analysis. Created messaging frameworks and campaign concepts tailored to US and India audiences. Positioned MediaTek as a company democratizing advanced technology for billions, emphasizing innovation impact over brand recognition.",
    outcome:
      "Launched employer brand campaigns that increased awareness and application rates in key talent markets. The EVP is now integrated across career sites, recruiting materials, and employee communications, helping MediaTek position as an employer of choice for engineers who want to make global impact.",
    images: ["/images/case-studies/mediatek-1.svg"],
    metrics: [
      { label: "Employee Interviews", value: "40+" },
      { label: "Target Markets", value: "2" },
      { label: "Brand Awareness Lift", value: "Elevated" },
      { label: "Application Rate", value: "Increased" },
    ],
  },
  {
    id: "satoro",
    client: "Satoro",
    title: "Democratizing Space Access",
    industry: "Space Technology / Commercial Aerospace",
    translationType: "tech-humanity",
    misunderstanding: "Complex space technology is inaccessible",
    hiddenTruth: "Commercial space technology that enables terrestrial breakthroughs.",
    translation: "Making commercial space technology accessible and actionable.",
    tag: "Space Innovation",
    tagline: "Making commercial space technology accessible and actionable.",
    services: "Brand Strategy • Market Education • Stakeholder Communication",
    gap: "Vision → Strategy",
    tier: "large",
    image: "/satoro-commercial-space.jpg",
    caseStudyUrl: "https://red-peak.com/satoro/",
    impact: [
      "Successful stakeholder engagement across government and enterprise",
      "Clear differentiation in emerging space market",
      "Enhanced credibility with technical and business audiences",
    ],
    year: "2024",
    role: "Strategic Communication Lead",
    duration: "4 months",
    challenge:
      "Satoro's commercial space technology faced skepticism and knowledge barriers. Complex technology and emerging market required clear value articulation for diverse stakeholders.",
    approach:
      "Created narrative bridging space technology with terrestrial applications. Developed educational content making space tech tangible and relevant. Positioned Satoro as the accessible entry point to commercial space.",
    outcome:
      "Successful stakeholder engagement across government and enterprise sectors. Clear differentiation in emerging space market with enhanced credibility.",
    images: ["/images/case-studies/satoro-1.svg"],
    metrics: [
      { label: "Stakeholder Reach", value: "100+" },
      { label: "Government Engagement", value: "Active" },
      { label: "Market Clarity", value: "Achieved" },
      { label: "Credibility Score", value: "Elevated" },
    ],
  },
  {
    id: "winbro",
    client: "WINBRO",
    title: "Precision Thermal Solutions",
    industry: "Thermal Solutions / Precision Manufacturing",
    translationType: "tech-humanity",
    misunderstanding: "Just another cooling equipment vendor",
    hiddenTruth: "Thermal intelligence that enables breakthrough performance across industries.",
    translation: "Leading thermal management innovation for advanced manufacturing.",
    tag: "Technology Leadership",
    tagline: "Leading thermal management innovation for advanced manufacturing.",
    services:
      "Brand Strategy • Market Positioning • Technical Communication • Product Storytelling",
    gap: "Technology → Humanity",
    tier: "large",
    image: "/winbro-thermal-cooling.jpg",
    caseStudyUrl: "https://red-peak.com/winbro/",
    impact: [
      "Enhanced brand recognition in precision manufacturing",
      "Strengthened partnerships with OEM customers",
      "Premium positioning in thermal solutions market",
      "Increased engagement with engineering decision-makers",
    ],
    year: "2024",
    role: "Brand Strategy Lead",
    duration: "5 months",
    challenge:
      "Winbro's advanced thermal solutions technology was recognized in specialized markets but lacked visibility in broader precision manufacturing sectors. Technical excellence in cooling systems wasn't translating to market leadership.",
    approach:
      "Positioned Winbro as the thermal intelligence partner for precision manufacturing. Developed narrative showcasing how thermal management enables breakthrough performance in CNC machining, semiconductor equipment, and medical devices. Created content strategy highlighting engineering expertise and customer success across industries.",
    outcome:
      "Enhanced brand recognition in precision manufacturing sector. Strengthened partnerships with OEM customers and achieved premium positioning in thermal solutions market.",
    images: ["/images/case-studies/winbro-1.svg"],
    metrics: [
      { label: "Market Visibility", value: "Elevated" },
      { label: "OEM Partnerships", value: "8+" },
      { label: "Premium Positioning", value: "Achieved" },
      { label: "Engineering Engagement", value: "Deepened" },
    ],
  },
  // === COMPACT CASE STUDIES ===
  {
    id: "okuma",
    client: "Okuma",
    title: "Inspired Fishing",
    industry: "Consumer",
    translationType: "local-global",
    misunderstanding: "Regional fishing equipment brand",
    hiddenTruth: "Passionate craftsmanship meeting global fishing enthusiasts.",
    translation: "Inspired fishing experiences worldwide.",
    tier: "compact",
    impact: ["Global market expansion", "Brand consistency", "Consumer engagement"],
    year: "2022",
    role: "Brand Strategist",
    duration: "3 months",
  },
  {
    id: "nebulum",
    client: "Nebulum",
    title: "Science to Investment",
    industry: "Biotech",
    translationType: "data-story",
    misunderstanding: "Complex science too abstract for investors",
    hiddenTruth: "Groundbreaking science ready for investment narrative.",
    translation: "Science to Investment.",
    tier: "compact",
    impact: ["Investor engagement", "Funding success", "Scientific credibility"],
    year: "2023",
    role: "Investment Communication Lead",
    duration: "2 months",
  },
]

export const translationTypes: TranslationType[] = [
  {
    id: "tech-humanity",
    fromLabel: "Technology",
    toLabel: "Humanity",
    description: "Making complex tech meaningful",
    clients: ["Innodisk", "MediaTek", "Aetina"],
    accentColor: "gold",
  },
  {
    id: "data-story",
    fromLabel: "Data",
    toLabel: "Story",
    description: "Turning numbers into narratives",
    clients: ["Chroma", "Wiltrom"],
    accentColor: "teal",
  },
  {
    id: "legacy-future",
    fromLabel: "Legacy",
    toLabel: "Future",
    description: "Connecting heritage to tomorrow",
    clients: ["Kikawa", "Inventec", "Quaser"],
    accentColor: "copper",
  },
  {
    id: "local-global",
    fromLabel: "Local",
    toLabel: "Global",
    description: "Helping Asian leaders speak to the world",
    clients: ["Lotus Pharma", "Ørsted", "Okuma"],
    accentColor: "process",
  },
  {
    id: "perception-reality",
    fromLabel: "Perception",
    toLabel: "Reality",
    description: "Closing the gap between what you are and what people see",
    clients: ["Innodisk", "Chroma", "Advantech"],
    accentColor: "gold",
  },
]

export const methodSteps = [
  {
    number: "01",
    title: "Deep Understanding",
    description:
      "Before I help the world understand you, I take the time to understand you myself. Not questionnaires. Dialogue. Not analysis. Listening.",
  },
  {
    number: "02",
    title: "Workflow Architecture",
    description:
      "I designed workflows for nuclear power plants. Now I design workflows for the AI era.",
  },
  {
    number: "03",
    title: "Multi-Perspective Exploration",
    description:
      "AI agents explore the problem from every angle. Machine intelligence provides scale. Human judgment provides direction.",
  },
  {
    number: "04",
    title: "Strategic Translation",
    description:
      "Finding the language that connects: Technology → Human value, Complexity → Clarity.",
  },
  {
    number: "05",
    title: "Compounding Partnership",
    description:
      "Every engagement makes the system smarter. You benefit from everything that came before.",
  },
]

export const capabilities = [
  {
    system: "MaestrOS",
    subtitle: "Meta-Intelligence Operating System",
    description: "Multiple AI agents collaborate while humans decide.",
    tags: ["Multi-Agent", "Knowledge Flywheel", "Human-in-Loop"],
  },
  {
    system: "BrandOS",
    subtitle: "Brand Intelligence System",
    description: "First application of MaestrOS for brand strategy.",
    tags: ["Deep Research", "Narrative Architect", "Stakeholder Sim"],
  },
]

export const services = [
  "Strategic Advisory",
  "Workflow Architecture",
  "Fractional Executive",
  "Intelligence Design",
  "Speaking & Workshops",
  "Translation Projects",
]
